/**
 * Portal Atendimentos API client (PRD 4, Story 4.1.5).
 *
 * Wraps backend routes under ``/api/v1/portal/atendimentos`` (Portal
 * Parceiro — auth via ``portal_access_token``, isolada do JWT interno).
 *
 * Diferente de ``atendimentosApi`` (painel interno), este service usa o
 * axios ``portalApi`` que injeta automaticamente o header
 * ``Authorization: Bearer <portal_access_token>`` e gerencia refresh
 * queue isolada do storage do colaborador.
 *
 * Backend ref: V1/backend/app/api/v1/endpoints/portal_atendimentos.py
 */

import portalApi from "@/services/portalApi";
import type {
  ChamadaEmAndamento,
  CustomerSession,
  CustomerSessionMessage,
  LiveKitTokenResponse,
  MessageListResponse,
  NaoLidasDoAtendimento,
  RecordConsentResponse,
  ScreenShareResponseApiResponse,
  SessionListResponse,
} from "@/types/atendimento";
import type { VoiceConsentStatus, VoiceUploadResponse } from "@/types/portal";

const PORTAL_PREFIX = "/portal/atendimentos";

export interface PortalSectorBrief {
  id: string;
  nome: string;
}

export interface PortalSectorListResponse {
  items: PortalSectorBrief[];
  count: number;
}

export interface PortalStartSessionRequest {
  sector_id: string;
  delivered_via?: "PORTAL" | "WHATSAPP_BRIDGE" | "TELEGRAM" | "EMAIL";
}

export interface PortalSendMessageBody {
  content: Record<string, unknown>;
  delivered_via?: "PORTAL" | "WHATSAPP_BRIDGE" | "TELEGRAM" | "EMAIL";
}

export const portalAtendimentosApi = {
  listMySessions: async (params?: {
    limit?: number;
    offset?: number;
  }): Promise<SessionListResponse> => {
    const r = await portalApi.get<SessionListResponse>(`${PORTAL_PREFIX}/my-sessions`, { params });
    return r.data;
  },

  /** As respostas do atendente que o parceiro ainda não leu (D3, passo 19, PR 1): o número do menu e o de cada conversa. */
  naoLidas: async (): Promise<NaoLidasDoAtendimento> => {
    const r = await portalApi.get<NaoLidasDoAtendimento>(`${PORTAL_PREFIX}/nao-lidas`);
    return r.data;
  },

  // Cursor: `before_id` (mais antigas, scroll pra cima) ou `after_id` (poll de
  // novidades). Sem cursor = as mais recentes. `offset` só compat legada.
  listMessages: async (
    sessionId: string,
    params?: {
      limit?: number;
      before_id?: string;
      after_id?: string;
      offset?: number;
      /**
       * D3 (PR 1, Decisão 10): `false` busca SEM gravar `read_at` nas respostas do atendente (o padrão do servidor é `true`). O
       * `usePortalMessages` manda `false` quando a busca acontece com a aba escondida (`paramsDeLeitura`).
       */
      marcar_lidas?: boolean;
    }
  ): Promise<MessageListResponse> => {
    const r = await portalApi.get<MessageListResponse>(`${PORTAL_PREFIX}/${sessionId}/messages`, {
      params,
    });
    return r.data;
  },

  listSectors: async (): Promise<PortalSectorListResponse> => {
    const r = await portalApi.get<PortalSectorListResponse>(`${PORTAL_PREFIX}/sectors`);
    return r.data;
  },

  startSession: async (body: PortalStartSessionRequest): Promise<CustomerSession> => {
    const r = await portalApi.post<CustomerSession>(`${PORTAL_PREFIX}/start`, body);
    return r.data;
  },

  sendMessage: async (
    sessionId: string,
    body: PortalSendMessageBody
  ): Promise<CustomerSessionMessage> => {
    const r = await portalApi.post<CustomerSessionMessage>(
      `${PORTAL_PREFIX}/${sessionId}/messages`,
      body
    );
    return r.data;
  },

  /**
   * Registra consent LGPD para gravação da sessão.
   * Idempotente — chamada repetida retorna o mesmo `consent_at`.
   */
  recordConsent: async (sessionId: string): Promise<RecordConsentResponse> => {
    const r = await portalApi.post<RecordConsentResponse>(`${PORTAL_PREFIX}/${sessionId}/consent`);
    return r.data;
  },

  /**
   * Pede token LiveKit para o parceiro entrar na sala da sessão.
   * Pode levantar AxiosError 412 com body `{ detail: ConsentRequiredErrorDetail }`
   * se o consent ainda não foi registrado — caller dispara ConsentModal.
   */
  /**
   * Há chamada em andamento nesta sessão? (07/08/2026)
   *
   * Consultado em polling para alimentar a FAIXA que avisa o parceiro. Barato
   * de propósito e **sem token** — o token vem do `getLiveKitToken`, chamado só
   * quando o parceiro clica em entrar.
   */
  getChamada: async (sessionId: string): Promise<ChamadaEmAndamento> => {
    const r = await portalApi.get<ChamadaEmAndamento>(`${PORTAL_PREFIX}/${sessionId}/chamada`);
    return r.data;
  },

  getLiveKitToken: async (sessionId: string): Promise<LiveKitTokenResponse> => {
    const r = await portalApi.get<LiveKitTokenResponse>(
      `${PORTAL_PREFIX}/${sessionId}/livekit-token`
    );
    return r.data;
  },

  /**
   * Story 4.5.2 — parceiro responde pedido de compartilhamento de tela.
   *
   * Chamar com `accepted=true` DEPOIS de já ter chamado
   * `navigator.mediaDevices.getDisplayMedia()` e publicado o track no
   * Room LiveKit. Com `accepted=false` representa recusa expressa.
   *
   * Backend emite `call_data_event(event_type='screen_share_accepted'|
   * 'screen_share_declined', sent_via='both')` — audit + dispatch.
   *
   * 202 (Accepted). Erro 404 quando a sessão não pertence ao parceiro
   * autenticado (defesa anti-enumeration).
   */
  respondToScreenShareRequest: async (
    sessionId: string,
    accepted: boolean
  ): Promise<ScreenShareResponseApiResponse> => {
    const r = await portalApi.post<ScreenShareResponseApiResponse>(
      `${PORTAL_PREFIX}/${sessionId}/screen-share-response`,
      { accepted }
    );
    return r.data;
  },

  /**
   * Story A4 — sobe uma nota de voz do parceiro ao storage e devolve a
   * `storage_key`. O parceiro NÃO alcança o `/storage/upload` interno (auth
   * `portalApi` separada) e a A1 exige `storage_key` (rejeita inline-b64) — por
   * isso este endpoint dedicado. A chave sai `voicenotes/{company_id}/…`, que
   * casa com o `_is_safe_key` da A1 (senão o send daria 422).
   */
  /** Sobe anexo (imagem/PDF) do chat ao storage e devolve a `storage_key`.
   *
   * Substitui o `attachment_b64` inline. O parceiro não alcança o
   * `/storage/upload` interno — o middleware do portal é DEFAULT-DENY e devolve
   * 403 fora de `/api/v1/portal` —, daí o endpoint dedicado. */
  uploadAttachment: async (file: File): Promise<VoiceUploadResponse> => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await portalApi.post<VoiceUploadResponse>(`${PORTAL_PREFIX}/attachment-upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return r.data;
  },

  uploadVoiceNote: async (file: File): Promise<VoiceUploadResponse> => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await portalApi.post<VoiceUploadResponse>(`${PORTAL_PREFIX}/voice-upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return r.data;
  },

  /** Story A4 — status do consent LGPD de nota de voz do parceiro. */
  getVoiceConsent: async (): Promise<VoiceConsentStatus> => {
    const r = await portalApi.get<VoiceConsentStatus>(`${PORTAL_PREFIX}/voice-consent`);
    return r.data;
  },

  /** Story A4 — concede o consent (idempotente; libera o envio de áudio). */
  grantVoiceConsent: async (): Promise<VoiceConsentStatus> => {
    const r = await portalApi.post<VoiceConsentStatus>(`${PORTAL_PREFIX}/voice-consent`);
    return r.data;
  },

  /** Story A4 — revoga o consent (bloqueia NOVAS gravações; áudio já enviado
   *  segue a retenção A5/F4). */
  revokeVoiceConsent: async (): Promise<VoiceConsentStatus> => {
    const r = await portalApi.delete<VoiceConsentStatus>(`${PORTAL_PREFIX}/voice-consent`);
    return r.data;
  },
};
